/**
 * The `ai-chat:send` detail of the ask-bar → AI Chat panel hand-off (#16102).
 *
 * Written by `useAskInChatHandoff` and read by `AiChatPanel`'s listener —
 * one builder and one reader in one module, so the two cannot drift.
 *
 * ## The shapes
 *
 * - A plain ask is the bare question string, or `{ message, surfaceContext }`
 *   with visible-page grounding — byte-for-byte what it was before files
 *   could ride along, so no existing sender or listener changes.
 * - With files it is always the object form, `surfaceContext` optional:
 *   `{ message, surfaceContext?, attachmentIds, attachments }`.
 *   `attachmentIds` is what the route is sent; `attachments` carries the
 *   names and types the transcript's chips show on the turn.
 *
 * ## The reader checks shape only
 *
 * A window event is a public channel, so the reader treats its detail as
 * unknown. `attachmentIds` is taken only when it is a non-empty array of
 * strings; anything else is ignored — never thrown on — and the message is
 * still sent. Ownership is not a client question: `/api/ai-chat` re-checks
 * that every id is the caller's own staged, unsent file (#16101) and refuses
 * the turn otherwise.
 */

import type { SentChatAttachment } from './chat-attachments'

export type AskInChatDetail =
  | string
  | {
      message: string
      surfaceContext?: string
      attachmentIds?: string[]
      attachments?: SentChatAttachment[]
    }

export interface AskInChatSend {
  message: string
  surfaceContext: string | undefined
  attachments: SentChatAttachment[] | undefined
}

/** The chip label for a file whose metadata did not arrive intact. */
const UNNAMED_FILE: Omit<SentChatAttachment, 'id'> = {
  fileName: 'Attached file',
  mimeType: 'application/octet-stream',
}

export function buildAskInChatDetail(
  message: string,
  surfaceContext?: string,
  attachments?: readonly SentChatAttachment[],
): AskInChatDetail {
  if (!attachments?.length) {
    return surfaceContext ? { message, surfaceContext } : message
  }
  return {
    message,
    ...(surfaceContext ? { surfaceContext } : {}),
    attachmentIds: attachments.map(file => file.id),
    attachments: attachments.map(({ id, fileName, mimeType }) => ({
      id,
      fileName,
      mimeType,
    })),
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined
  if (!value.every(id => typeof id === 'string')) return undefined
  return value as string[]
}

function metadataById(value: unknown): Map<string, SentChatAttachment> {
  const byId = new Map<string, SentChatAttachment>()
  if (!Array.isArray(value)) return byId
  for (const entry of value) {
    if (
      isRecord(entry) &&
      typeof entry.id === 'string' &&
      typeof entry.fileName === 'string' &&
      typeof entry.mimeType === 'string'
    ) {
      byId.set(entry.id, {
        id: entry.id,
        fileName: entry.fileName,
        mimeType: entry.mimeType,
      })
    }
  }
  return byId
}

/** The send a detail asks for, or null when it is not one. */
export function readAskInChatDetail(detail: unknown): AskInChatSend | null {
  if (typeof detail === 'string') {
    return {
      message: detail,
      surfaceContext: undefined,
      attachments: undefined,
    }
  }
  if (!isRecord(detail) || typeof detail.message !== 'string') return null

  const ids = readIds(detail.attachmentIds)
  const named = ids ? metadataById(detail.attachments) : null
  return {
    message: detail.message,
    surfaceContext:
      typeof detail.surfaceContext === 'string'
        ? detail.surfaceContext
        : undefined,
    attachments: ids?.map(id => named?.get(id) ?? { id, ...UNNAMED_FILE }),
  }
}
